import { useState } from "react";
import { useForm } from "react-hook-form";
import { ScaleLoader } from "react-spinners";
import { ReactComponent as EyeOpen } from "assets/svgs/eye-open.svg";
import { ReactComponent as EyeClose } from "assets/svgs/eye-close.svg";

const INPUT_BASE =
  "w-full h-[44px] px-3 bg-shade-50 border rounded-ns text-[15px] text-ink outline-none transition-colors duration-200 placeholder-shade-900";
const INPUT_OK =
  "border-rule-strong focus:border-accent focus:shadow-[0_0_0_3px_rgba(123,94,167,0.12)]";
const INPUT_ERR = "border-danger";

const LoginForm = ({ onSubmit, loading }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const [hidePassword, setHidePassword] = useState(true);

  return (
    <>
      <div className="mb-5">
        <h2 className="text-[20px] font-bold text-ink mb-1">Welcome back</h2>
        <p className="text-xs text-muted">
          Don't have an account?{" "}
          <a
            href="/register"
            className="text-accent font-semibold hover:underline"
          >
            Sign up free
          </a>
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* Email */}
        <div className="mb-3">
          <label
            htmlFor="email"
            className="block text-[12px] font-medium text-muted mb-[5px]"
          >
            Email Address <span className="text-danger">*</span>
          </label>
          <input
            id="email"
            type="email"
            {...register("email", {
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Invalid email format",
              },
            })}
            placeholder="Email"
            aria-describedby={errors.email ? "email-error" : undefined}
            className={`${INPUT_BASE} ${errors.email ? INPUT_ERR : INPUT_OK}`}
          />
          {errors.email && (
            <p id="email-error" role="alert" className="text-danger text-[11px] mt-1">
              {errors.email.message}
            </p>
          )}
        </div>

        {/* Password */}
        <div className="mb-4">
          <label
            htmlFor="password"
            className="block text-[12px] font-medium text-muted mb-[5px]"
          >
            Password <span className="text-danger">*</span>
          </label>
          <div className="relative">
            <input
              id="password"
              type={hidePassword ? "password" : "text"}
              {...register("password", {
                required: "Password is required",
              })}
              placeholder="Your password"
              aria-describedby={errors.password ? "password-error" : undefined}
              className={`${INPUT_BASE} pr-10 ${errors.password ? INPUT_ERR : INPUT_OK}`}
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setHidePassword((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-muted"
              aria-label={hidePassword ? "Show password" : "Hide password"}
            >
              {hidePassword ? <EyeOpen /> : <EyeClose />}
            </button>
          </div>
          {errors.password && (
            <p id="password-error" role="alert" className="text-danger text-[11px] mt-1">
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Remember me + Forgot password */}
        <div className="flex items-center justify-between mb-5">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              {...register("rememberMe")}
              className="w-4 h-4 rounded border-rule-strong accent-brand-purple cursor-pointer"
            />
            <span className="text-[13px] text-muted">Remember me</span>
          </label>
          <a
            href="/forgot-password"
            className="text-[13px] text-accent font-medium hover:underline"
          >
            Forgot password?
          </a>
        </div>

        {/* CTA */}
        <button
          type="submit"
          disabled={loading}
          aria-busy={loading}
          className="w-full h-12 rounded-ns bg-accent text-white text-[15px] font-bold relative border border-rule-strong  transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-70"
        >
          <span className={loading ? "opacity-0" : "opacity-100"}>
            Sign In →
          </span>
          {loading && (
            <ScaleLoader
              loading
              height={16}
              color="white"
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            />
          )}
        </button>
      </form>
    </>
  );
};

export default LoginForm;
